/**
 * Pure normalization behind the Supplier Discovery step: research output →
 * typed view models. Every field here is traceable to stored research data —
 * when a value is missing the view field stays undefined and the UI renders
 * an honest absent-state, never an invented number (honest-UI rule).
 */

import { getCurrencySymbol } from '../../utils/currency';
import { WebsiteKind, websiteOf } from './supplierWebsite';
import type { ResearchState } from '../../types/bom.types';
import { PartNumberCheckView, partNumberCheckOf } from './partNumberCheck';

export interface PriceTierView {
  qty: number;
  unitPrice: number;
  currencySymbol: string;
}

/** Best justified lower price — research must cite its basis or store null. */
export interface NegotiableToView {
  /** Robbie's recommended ask; null when the analyst saw room but named no number. */
  price: number | null;
  basis: string;
}

export type SpecMet = 'met' | 'exceeds' | 'miss' | 'unknown';

/** A key spec this supplier offers that differs from the buyer's BOM line (backend spec check). */
export interface SpecMismatchView {
  key: string;
  label: string;
  requested: string;
  offered: string;
  /**
   * exceeds — a higher rating than asked: shown, never gated. below /
   * different — not what the buyer asked for: outreach asks for theirs.
   * Absent on rows stored before the kind existed; read as different.
   */
  kind?: 'exceeds' | 'below' | 'different';
}

/** The buyer's answer to a spec mismatch; null until they decide (outreach then asks for theirs). */
export type SpecDecision = 'ASK_FOR_OURS' | 'ACCEPT_THEIRS' | null;

export interface SpecMatchView {
  requirement: string;
  offered?: string;
  met: SpecMet;
}

export interface ComplianceView {
  name: string;
  held: boolean | 'unknown';
}

export interface TrustPointView {
  text: string;
  sourceUrl?: string;
}

/** Why the supplier has its trust score: evidence only, never the method behind it. */
export interface TrustDetailsView {
  band?: string;
  confidence?: 'low' | 'medium' | 'high';
  status?: 'scored' | 'review' | 'blocked';
  summary?: string;
  supports: TrustPointView[];
  concerns: TrustPointView[];
}

export interface SupplierView {
  /** The supplier's name; the name-keyed shortlist fallback. */
  name: string;
  /** One product row — the id the shortlist saves (backend listing annotation). */
  listingId?: string;
  /** Same value on every listing of one supplier (id, own website or name; never a marketplace). */
  supplierKey?: string;
  /** '' when the listing matches the BOM line's spec; otherwise the spec it offers instead. */
  specGroup?: string;
  location?: string;
  /** Saved at shortlisting: outside where the buyer asked to source from. */
  outsideSourcingLocation?: boolean;
  /** 0–100, only when research actually scored the supplier. */
  trustScore?: number;
  /** Saved at shortlisting: scored under the buyer's trust bar (TRUST_BAR). */
  belowTrustBar?: boolean;
  /** Reasons behind the score, when research computed one from evidence. */
  trust?: TrustDetailsView;
  /**
   * Robbie's rank among the listings of the same spec: the server's `specRank`
   * (stored picks, or the shortlisting rules when those belong to another spec),
   * else the stored rank from a payload before it existed.
   */
  rank?: number;
  /** One-line reasoning from research; omitted rows render without one. */
  reason?: string;
  advantages: string[];
  risks: string[];
  priceLabel?: string;
  leadTimeLabel?: string;
  /** Numeric price/lead for computed context (delta vs target, slack). */
  quotedPrice?: number;
  currencySymbol: string;
  leadTimeWeeks?: number;
  moq?: number;
  /** The product page for this part when research found one, else the supplier's site. */
  websiteUrl?: string;
  /** Which of the two `websiteUrl` is — the card labels the link by it. */
  websiteKind?: WebsiteKind;
  /**
   * Whether the platform holds a contact address for this supplier. The
   * address itself is deliberately never sent to the browser — the supplier
   * network is the platform's asset and Robbie is the one who writes to them.
   */
  hasContactEmail: boolean;
  /** Robbie is looking the contact up right now; shown instead of "No email". */
  findingContact?: boolean;
  // Negotiation-intelligence fields — absent on older research data; every
  // consumer renders an honest "—" or hides the slot when one is missing.
  priceTiers?: PriceTierView[];
  negotiableTo?: NegotiableToView;
  /** Ask-phrased lines lifted from the analyst's report; render verbatim. */
  negotiationAngles?: string[];
  freightTerms?: string;
  warranty?: string;
  stockStatus?: string;
  specMatch?: SpecMatchView[];
  compliance?: ComplianceView[];
  /** Whether the listing shows the BOM line's part number; absent when the line has none. */
  partNumberCheck?: PartNumberCheckView;
  /** Empty/absent when the offer matches the BOM, or neither side states a comparable spec. */
  specMismatches?: SpecMismatchView[];
  specDecision?: SpecDecision;
}

/** `queued`: waiting its turn — the user already has their limit of searches running. */
export type ComponentPhase = 'queued' | 'searching' | 'failed' | 'empty' | 'ready';

/** One key spec the BOM line states ("30C"), as the server reads it off the line's wording. */
export interface LineKeySpec {
  key: string;
  label: string;
  text: string;
}

/** The line's key specs in wording order; [] when the payload carries none. */
export const keySpecsOf = (c: unknown): LineKeySpec[] => {
  const rows = (c as { keySpecs?: unknown } | null)?.keySpecs;
  if (!Array.isArray(rows)) return [];
  return rows.filter((k): k is LineKeySpec => {
    const row = k as Partial<LineKeySpec> | null;
    return typeof row?.key === 'string' && typeof row?.label === 'string' && typeof row?.text === 'string' && row.text.trim() !== '';
  });
};

export interface ComponentListItem {
  key: string;
  name: string;
  partNumber?: string;
  mpn?: string;
  phase: ComponentPhase;
  supplierCount: number;
  pickedCount: number;
  /** Suppliers whose listing matches the line's spec; absent → supplierCount stands. */
  matchingSupplierCount?: number;
  /** Other specs research found for the line ("2 other specs"). */
  otherSpecCount?: number;
  /** Live research state from the status poll, when the server sent one. */
  researchState?: ResearchState;
  attempt?: number | null;
  maxAttempts?: number;
}

/** Same identity the shortlist endpoints receive — do not change one side only. */
export const componentKeyOf = (c: any, idx: number): string =>
  c?.id || c?.componentId || c?._id || `comp-${idx}`;

export const suppliersOf = (c: any): any[] =>
  c?.supplierCandidates || c?.supplierResearch?.suppliers || [];

export const isSupplierShortlisted = (s: any): boolean =>
  s?.isShortlisted || s?.status === 'SHORTLISTED' || s?.status === 'shortlisted';

export const componentPhaseOf = (c: any): ComponentPhase => {
  const status = String(c?.researchMetadata?.status || c?.status || '').toLowerCase();
  if (status === 'failed') return 'failed';
  const hasSuppliers = suppliersOf(c).length > 0;
  if (hasSuppliers) return 'ready';
  if (status === 'completed' || status === 'analyzed') return 'empty';
  return c?.queued ? 'queued' : 'searching';
};

/**
 * The part's name without its trailing spec ("Brushless motor 2806.5 1300KV" →
 * "Brushless motor"), for buttons that must stay one line.
 */
export const shortPartName = (name: string): string => {
  const words = name.trim().split(/\s+/);
  const firstSpec = words.findIndex((w) => /\d/.test(w));
  const head = firstSpec > 0 ? words.slice(0, firstSpec).join(' ') : name.trim();
  return head.length > 28 ? `${head.slice(0, 27)}…` : head;
};

/** Still waiting for results, whether queued or being searched. */
export const isPhaseInProgress = (phase: ComponentPhase): boolean => phase === 'queued' || phase === 'searching';

const asFiniteNumber = (v: any): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;

/**
 * The prefix every price in discovery is rendered with. It is never empty for a
 * currency we were actually told: a symbol when one is unambiguous, otherwise
 * the ISO code ("CNY 442.92").
 *
 * The old rule was "known symbol or nothing", which was written to stop an
 * unknown currency defaulting to ₹ — right instinct, wrong fallback. Since the
 * research engine started pricing reports in the delivery country's currency,
 * anything outside these four rendered as a naked "442.92" with no unit at all,
 * which reads as rupees to anyone who has been using this app. A code is the
 * honest answer; nothing is not.
 */
const currencySymbolOf = (currency: any): string => {
  if (!currency) return '';
  const explicit = typeof currency === 'string' ? undefined : currency.symbol;
  if (explicit) return explicit;

  const raw = typeof currency === 'string' ? currency : currency.code;
  if (typeof raw !== 'string' || !raw.trim()) return '';
  return getCurrencySymbol(raw);
};

const priceLabelOf = (s: any): string | undefined => {
  const amount = asFiniteNumber(s?.quotedPrice) ?? asFiniteNumber(s?.price_per_unit);
  if (amount == null) return undefined;
  return `${currencySymbolOf(s?.currency)}${amount.toLocaleString()}/unit`;
};

const leadTimeLabelOf = (s: any): string | undefined => {
  const weeks = asFiniteNumber(s?.leadTimeWeeks) ?? asFiniteNumber(s?.lead_time_weeks);
  if (weeks != null) return `${weeks}-wk lead`;
  const days = asFiniteNumber(s?.delivery_time_days);
  if (days != null) return `${days}-day delivery`;
  return undefined;
};

const trimmedTextOf = (v: any): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;

const priceTiersOf = (s: any): PriceTierView[] | undefined => {
  if (!Array.isArray(s?.priceTiers)) return undefined;
  const tiers = s.priceTiers
    .map((t: any) => ({
      qty: asFiniteNumber(t?.qty),
      unitPrice: asFiniteNumber(t?.unitPrice),
      currencySymbol: currencySymbolOf(t?.currency ?? s?.currency),
    }))
    .filter((t: any): t is PriceTierView => t.qty != null && t.unitPrice != null);
  return tiers.length > 0 ? tiers : undefined;
};

/**
 * The contract requires a cited basis — Robbie's ask is a judgment and the
 * "why" is what makes it usable. A number without a basis stays absent; a
 * basis without a number renders alone (the analyst saw room but declined
 * to commit to a figure).
 */
const negotiableToOf = (s: any): NegotiableToView | undefined => {
  const price = asFiniteNumber(s?.negotiableTo?.price);
  const basis = trimmedTextOf(s?.negotiableTo?.basis);
  return basis ? { price: price ?? null, basis } : undefined;
};

const negotiationAnglesOf = (s: any): string[] | undefined => {
  if (!Array.isArray(s?.negotiationAngles)) return undefined;
  const lines = s.negotiationAngles.map(trimmedTextOf).filter(Boolean) as string[];
  return lines.length > 0 ? lines : undefined;
};

const specMetOf = (v: any): SpecMet =>
  v === 'met' || v === 'exceeds' || v === 'miss' ? v : 'unknown';

const specMatchOf = (s: any): SpecMatchView[] | undefined => {
  if (!Array.isArray(s?.specMatch)) return undefined;
  const rows = s.specMatch
    .map((r: any) => ({
      requirement: trimmedTextOf(r?.requirement),
      offered: trimmedTextOf(r?.offered),
      met: specMetOf(r?.met),
    }))
    .filter((r: any): r is SpecMatchView => Boolean(r.requirement));
  return rows.length > 0 ? rows : undefined;
};

const specMismatchesOf = (rows: unknown): SpecMismatchView[] =>
  Array.isArray(rows)
    ? rows.filter((m): m is SpecMismatchView =>
        Boolean(m && trimmedTextOf((m as SpecMismatchView).requested) && trimmedTextOf((m as SpecMismatchView).offered)))
    : [];

const specDecisionOf = (value: unknown): SpecDecision =>
  value === 'ASK_FOR_OURS' || value === 'ACCEPT_THEIRS' ? value : null;

const mismatchClause = (m: SpecMismatchView): string => {
  if (m.kind === 'exceeds') return `Offers ${m.offered} — above your ${m.requested}`;
  if (m.kind === 'below') return `Offers ${m.offered} — below your ${m.requested}`;
  return `Offers ${m.offered}; you asked for ${m.requested}`;
};

/** "Offers 40C — above your 30C" / "Offers 14.8V; you asked for 11.1V" — one clause per differing spec. */
export const specMismatchLabel = (mismatches: SpecMismatchView[]): string =>
  mismatches.map(mismatchClause).join(' · ');

/** The differences that need the buyer's say — everything but a higher rating than asked. */
export const gatingSpecMismatches = (mismatches: SpecMismatchView[] = []): SpecMismatchView[] =>
  mismatches.filter((m) => m.kind !== 'exceeds');

const complianceOf = (s: any): ComplianceView[] | undefined => {
  if (!Array.isArray(s?.compliance)) return undefined;
  const rows = s.compliance
    .map((c: any) => ({
      name: trimmedTextOf(c?.name),
      held: c?.held === true || c?.held === false ? c.held : ('unknown' as const),
    }))
    .filter((c: any): c is ComplianceView => Boolean(c.name));
  return rows.length > 0 ? rows : undefined;
};

const trustPointsOf = (points: unknown): TrustPointView[] =>
  Array.isArray(points)
    ? points
        .map((raw: unknown) => {
          const p = (raw ?? {}) as { text?: unknown; sourceUrl?: unknown };
          return {
            text: trimmedTextOf(p.text) as string,
            sourceUrl: typeof p.sourceUrl === 'string' && /^https?:\/\//i.test(p.sourceUrl) ? p.sourceUrl : undefined,
          };
        })
        .filter((p) => Boolean(p.text))
    : [];

const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
  allowed.includes(value as T) ? (value as T) : undefined;

const trustOf = (s: Record<string, unknown> | undefined): TrustDetailsView | undefined => {
  const j = s?.trustJustification as { summary?: unknown; supports?: unknown; concerns?: unknown } | undefined;
  if (!j || typeof j !== 'object') return undefined;
  return {
    band: trimmedTextOf(s?.trustBand),
    confidence: oneOf(s?.trustConfidence, ['low', 'medium', 'high'] as const),
    status: oneOf(s?.trustStatus, ['scored', 'review', 'blocked'] as const),
    summary: trimmedTextOf(j.summary),
    supports: trustPointsOf(j.supports),
    concerns: trustPointsOf(j.concerns),
  };
};

export const normalizeSupplier = (s: any): SupplierView => ({
  name: s?.supplierName || s?.name || 'Unknown supplier',
  listingId: trimmedTextOf(s?.listingId),
  supplierKey: trimmedTextOf(s?.supplierKey),
  specGroup: typeof s?.specGroup === 'string' ? s.specGroup : undefined,
  location: s?.location || undefined,
  outsideSourcingLocation: s?.outsideSourcingLocation === true,
  trustScore: asFiniteNumber(s?.trustScore) ?? asFiniteNumber(s?.supplierTrust?.trustScore) ?? asFiniteNumber(s?.reliability_score),
  belowTrustBar: s?.belowTrustBar === true,
  trust: trustOf(s),
  rank: asFiniteNumber(s?.specRank) ?? asFiniteNumber(s?.rank) ?? asFiniteNumber(s?.shortlistRank),
  reason: s?.aiReasoning || s?.shortlistReasoning || undefined,
  advantages: Array.isArray(s?.advantages) ? s.advantages : [],
  risks: Array.isArray(s?.risks) ? s.risks : [],
  priceLabel: priceLabelOf(s),
  leadTimeLabel: leadTimeLabelOf(s),
  quotedPrice: asFiniteNumber(s?.quotedPrice) ?? asFiniteNumber(s?.price_per_unit),
  currencySymbol: currencySymbolOf(s?.currency),
  leadTimeWeeks: asFiniteNumber(s?.leadTimeWeeks) ?? asFiniteNumber(s?.lead_time_weeks),
  moq: asFiniteNumber(s?.moq),
  ...websiteOf(s),
  hasContactEmail: s?.hasContactEmail === true,
  findingContact: s?.hasContactEmail !== true && s?.findingContact === true,
  priceTiers: priceTiersOf(s),
  negotiableTo: negotiableToOf(s),
  negotiationAngles: negotiationAnglesOf(s),
  freightTerms: trimmedTextOf(s?.freightTerms),
  warranty: trimmedTextOf(s?.warranty),
  stockStatus: trimmedTextOf(s?.stockStatus),
  specMatch: specMatchOf(s),
  compliance: complianceOf(s),
  partNumberCheck: partNumberCheckOf(s?.partNumberCheck),
  specMismatches: specMismatchesOf(s?.specMismatches),
  specDecision: specDecisionOf(s?.specDecision),
});

export interface SupplierSplit {
  /** Explicitly ranked #1/#2 by research — the only suppliers sold as picks. */
  picks: SupplierView[];
  others: SupplierView[];
  /** False when research produced no ranks — then nothing claims a ranking. */
  ranked: boolean;
}

export const splitSuppliers = (list: SupplierView[]): SupplierSplit => {
  const sorted = [...list].sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
  const ranked = sorted.some((s) => s.rank != null);
  if (!ranked) return { picks: [], others: sorted, ranked };
  return {
    picks: sorted.filter((s) => (s.rank ?? 99) <= 2),
    others: sorted.filter((s) => (s.rank ?? 99) > 2),
    ranked,
  };
};

